// Hosts the correct "add/edit a single record" side panel for each
// record-based change section (risk location, coverage, schedule, interested
// party), and writes the saved record into the matching
// values.*_change.records array.
//
// This is what makes the board's "Add" button (and "Edit" in Changes
// Requested) open the record form directly, instead of an intermediate table.
// The board's "Change" (Substitution) button first opens a picker to select
// the existing record being replaced (stored on <section>_change.delete_*),
// then lands on the same record form used for "Add".
//
// PERFORMANCE NOTE: the substitution pickers need the "existing records" lists
// (risk locations, coverages, schedules, interested parties). Those lists are
// fetched by the useHabitational* hooks. To avoid firing all four list APIs on
// manage-view load, each section's UI lives in its OWN component below and
// only that component calls its hook. Because a section component is mounted
// only when it is the active `target`, its list API fires only when that
// specific panel opens — and only when actually substituting ("Change" mode).
// Plain "Add"/"Edit" renders the *RightPanel directly and needs no list.

import React from "react";
import produce from "immer";
import { IHabitationalPolicyChange } from "../../../../../../../../redux/habitationalPolicy/habitationalPolicyChangeRequest/habitationalPolicyChangeReq.types";
import { HabitationalRiskLocationRightPanel } from "../../../HabitationalRiskLocation/HabitationalRiskLocationRightPanel";
import { HabitationalCoverageRightPanel } from "../../../HabitationalCoverages/HabitationalCoverageRightPanel";
import { HabitationalScheduleDetailRightPanel } from "../../../HabitationalScheduleDetail/HabitationalScheduleDetailRightPanel.tsx";
import { InterestedPartiesRightPanel } from "../../../InterestedParties/InterestedPartiesRightPanel";
import { ChangeSectionKey } from "./WhatChangedBoard";
import { RecordReplacePanelV2 } from "./RecordReplacePanelV2";
import { HabitationalRiskLocationForm } from "../../../HabitationalRiskLocation/HabitationalRiskLocation";
import { HabitationalCoverages } from "../../../HabitationalCoverages/HabitationalCoverages";
import { HabitationalScheduleDetail } from "../../../HabitationalScheduleDetail/HabitationalScheduleDetail";
import { InterestedParties } from "../../../InterestedParties/InterestedParties";
import { useHabitationalPolicyRiskLocation } from "../../../hooks/useHabitationalPolicyRiskLocation";
import { useHabitationalCoevrage } from "../../../hooks/useHabitationalCoverage";
import { useHabitationalSchedule } from "../../../hooks/useHabitationalSchedule";
import { useHabitationalIntrestedParty } from "../../../hooks/useHabitationalIntrestedParty";

// Sections that use a record panel (single-object sections are handled inline).
export type RecordSectionKey =
  | "riskLocationChange"
  | "coverageChange"
  | "scheduleChange"
  | "interestedPartyChange";

export const isRecordSection = (
  section: ChangeSectionKey
): section is RecordSectionKey =>
  [
    "riskLocationChange",
    "coverageChange",
    "scheduleChange",
    "interestedPartyChange",
  ].includes(section);

export interface IRecordPanelTarget {
  section: RecordSectionKey;
  // undefined => create a new record; number => edit the record at this index.
  editIndex?: number;
  // Optional mode carried over from the board selection (e.g. "Change" for a
  // substitution). Falls back to "Add" when not provided.
  mode?: string | null;
}

// Maps a section to the values.*_change slice key.
const SECTION_TO_SLICE: Record<
  RecordSectionKey,
  | "risk_location_change"
  | "coverage_change"
  | "schedule_change"
  | "interested_party_change"
> = {
  riskLocationChange: "risk_location_change",
  coverageChange: "coverage_change",
  scheduleChange: "schedule_change",
  interestedPartyChange: "interested_party_change",
};

// Maps a section to the "which existing record is being replaced" field on
// its slice, used for the "Change" (substitution) flow.
const SECTION_TO_DELETE_KEY: Record<
  RecordSectionKey,
  | "delete_risk_location"
  | "delete_coverage"
  | "delete_schedule"
  | "delete_interested_party"
> = {
  riskLocationChange: "delete_risk_location",
  coverageChange: "delete_coverage",
  scheduleChange: "delete_schedule",
  interestedPartyChange: "delete_interested_party",
};

interface IRecordPanelHostProps {
  target: IRecordPanelTarget | null;
  values: IHabitationalPolicyChange;
  customerId?: number;
  customerPolicyId?: number;
  setValues: (values: any, shouldValidate?: boolean) => any;
  autoSaveData?: (values: IHabitationalPolicyChange) => void;
  onClose: () => void;
}

// Shared props each per-section panel needs. `pickedDelete`/`setPickedDelete`
// live on the host so the substitution target survives a stale `values`
// closure (see saveRecord note below).
interface ISectionPanelProps {
  target: IRecordPanelTarget;
  values: IHabitationalPolicyChange;
  customerId?: number;
  customerPolicyId?: number;
  isEdit: boolean;
  existing: any;
  pickedDelete: string | number | null;
  setPickedDelete: (v: string | number | null) => void;
  setValues: (values: any, shouldValidate?: boolean) => any;
  saveRecord: (record: any) => void;
  onClose: () => void;
}

export const RecordPanelHost: React.FC<IRecordPanelHostProps> = ({
  target,
  values,
  customerId,
  customerPolicyId,
  setValues,
  autoSaveData = () => {},
  onClose,
}) => {
  // Locally remembers which existing record the user picked to replace in the
  // Substitution picker. Kept in local state (rather than relying solely on
  // the parent `values`) so the record being replaced can't be lost to a
  // stale `values` closure or a store round-trip between the picker step and
  // the save step — which previously caused a substitution to surface as a
  // plain ADD plus a stray DELETE row.
  const [pickedDelete, setPickedDelete] = React.useState<string | number | null>(
    null
  );

  // Reset the remembered substitution target whenever the panel target
  // changes (new section, or switching between add/edit).
  React.useEffect(() => {
    setPickedDelete(null);
  }, [target?.section, target?.editIndex, target?.mode]);

  if (!target) {
    return null;
  }

  const sliceKey = SECTION_TO_SLICE[target.section];
  const deleteKey = SECTION_TO_DELETE_KEY[target.section];
  const isEdit = target.editIndex !== undefined;
  const existing =
    isEdit && (values as any)[sliceKey]?.records
      ? (values as any)[sliceKey].records[target.editIndex as number]
      : undefined;

  // Enriches the saved record and writes it back into the right records array.
  const saveRecord = (record: any) => {
    const newValues = produce(values, (draftValues: any) => {
      // Each record carries its OWN mode ("Add" / "Change") and, for a
      // substitution, its OWN snapshot of the record being replaced. This is
      // what keeps a later "Add" from retroactively flipping earlier "Change"
      // records to ADD in the Changes Requested summary — the section-level
      // `type` flag alone can't distinguish per-record modes.
      const recordMode = target.mode === "Change" ? "Change" : "Add";
      const existingReplacedSnapshot =
        isEdit && (record as any)?.replaced_record_details
          ? (record as any).replaced_record_details
          : undefined;
      const replacedSnapshot =
        recordMode === "Change"
          ? existingReplacedSnapshot ??
            (draftValues[sliceKey]?.delete_risk_location_details ??
              draftValues[sliceKey]?.delete_coverage_details ??
              draftValues[sliceKey]?.delete_schedule_details ??
              draftValues[sliceKey]?.delete_interested_party_details ??
              null)
          : undefined;
      const updatedData = {
        ...record,
        customer_policy_id: customerPolicyId ?? null,
        customer_id: customerId ? Number(customerId) : null,
        // Per-record mode + which existing record this one replaces.
        mode: recordMode,
        replaced_delete_value:
          recordMode === "Change"
            ? pickedDelete ?? (record as any)?.replaced_delete_value ?? null
            : null,
        replaced_record_details:
          recordMode === "Change" ? replacedSnapshot : null,
      };
      // Defensively initialise the slice if the server returned it as null.
      if (!draftValues[sliceKey]) {
        draftValues[sliceKey] = { type: null, records: [] };
      }
      if (!Array.isArray(draftValues[sliceKey].records)) {
        draftValues[sliceKey].records = [];
      }
      // Default the section mode to "Add" if not already set. When the
      // record panel was opened for a "Change" (substitution) action, keep
      // that mode instead of defaulting to "Add" so it surfaces correctly in
      // "Changes Requested" and is submitted with the right type.
      if (!draftValues[sliceKey].type || target.mode) {
        draftValues[sliceKey].type =
          target.mode || draftValues[sliceKey].type || "Add";
      }
      // Re-assert the substitution target from local state on save. This makes
      // the "Change" flow self-contained: even if the parent `values` this
      // closure captured is stale (e.g. a store round-trip happened between
      // picking the record and saving), the section still ends up with
      // type="Change" AND its delete_* pointer set together — never one
      // without the other (which previously showed as ADD + a stray DELETE).
      if (target.mode === "Change" && pickedDelete !== null) {
        draftValues[sliceKey].type = "Change";
        draftValues[sliceKey][deleteKey] = pickedDelete;
      }
      if (isEdit) {
        draftValues[sliceKey].records[target.editIndex as number] = updatedData;
      } else {
        draftValues[sliceKey].records.push(updatedData);
      }
    });
    autoSaveData(newValues);
    setValues(newValues);
    onClose();
  };

  const sectionProps: ISectionPanelProps = {
    target,
    values,
    customerId,
    customerPolicyId,
    isEdit,
    existing,
    pickedDelete,
    setPickedDelete,
    setValues,
    saveRecord,
    onClose,
  };

  // Only the matching section component mounts, so only its list hook (if any)
  // runs — and only when substituting.
  switch (target.section) {
    case "riskLocationChange":
      return <RiskLocationRecordPanel {...sectionProps} />;
    case "coverageChange":
      return <CoverageRecordPanel {...sectionProps} />;
    case "scheduleChange":
      return <ScheduleRecordPanel {...sectionProps} />;
    case "interestedPartyChange":
      return <InterestedPartyRecordPanel {...sectionProps} />;
    default:
      return null;
  }
};

// ---------------------------------------------------------------------------
// Per-section panels. Each is mounted only when its section is the active
// target, so its list hook fires lazily (only when that panel opens, and only
// when substituting). The plain "Add"/"Edit" path renders the *RightPanel
// directly and needs no list.
// ---------------------------------------------------------------------------

const RiskLocationRecordPanel: React.FC<ISectionPanelProps> = ({
  values,
  customerId,
  customerPolicyId,
  isEdit,
  existing,
  target,
  pickedDelete,
  setPickedDelete,
  setValues,
  saveRecord,
  onClose,
}) => {
  const isSubstitution = target.mode === "Change";
  const { riskLocationsDropDown, riskLocations } =
    useHabitationalPolicyRiskLocation(
      isSubstitution ? customerPolicyId : undefined
    );

  if (isSubstitution) {
    return (
      <RecordReplacePanelV2
        open={true}
        heading="Risk Location Change"
        recordLabel="Risk Location"
        options={riskLocationsDropDown}
        valueToReplace={
          values.risk_location_change?.delete_risk_location ??
          pickedDelete ??
          null
        }
        disableSelect={isEdit}
        onSelectToReplace={(riskLocationToReplace) => {
          setPickedDelete(riskLocationToReplace);
          const matched = riskLocations.find(
            (r) =>
              String(r.incremental_number ?? "") ===
              String(riskLocationToReplace)
          );
          const newValues = produce(values, (draftValues: any) => {
            if (!draftValues.risk_location_change) {
              draftValues.risk_location_change = { type: null, records: [] };
            }
            draftValues.risk_location_change.type = "Change";
            draftValues.risk_location_change.delete_risk_location =
              riskLocationToReplace;
            draftValues.risk_location_change.delete_risk_location_details = {
              incremental_number:
                matched?.incremental_number != null
                  ? String(matched.incremental_number)
                  : String(riskLocationToReplace),
              address: matched?.address ?? null,
              city: matched?.city ?? null,
              province_or_state: matched?.province_or_state ?? null,
              postal_code: matched?.postal_code ?? null,
            };
          });
          setValues(newValues);
        }}
        onClose={onClose}
      >
        <HabitationalRiskLocationForm
          customerId={customerId}
          customerPolicyId={customerPolicyId}
          incrementalNumber={
            existing ? (existing.incremental_number as any) : undefined
          }
          onSaveSuccess={saveRecord}
          fromRightPanel
          created_from="Policy_Change"
          initialData={existing}
        />
      </RecordReplacePanelV2>
    );
  }

  return (
    <HabitationalRiskLocationRightPanel
      open={true}
      customerId={customerId}
      customerPolicyId={customerPolicyId}
      incrementalNumber={
        existing ? (existing.incremental_number as any) : undefined
      }
      onSaveSuccess={saveRecord}
      created_from="Policy_Change"
      onClose={onClose}
    />
  );
};

const CoverageRecordPanel: React.FC<ISectionPanelProps> = ({
  values,
  customerId,
  customerPolicyId,
  isEdit,
  existing,
  target,
  pickedDelete,
  setPickedDelete,
  setValues,
  saveRecord,
  onClose,
}) => {
  const isSubstitution = target.mode === "Change";
  const { coverageDropDown } = useHabitationalCoevrage(
    isSubstitution ? customerPolicyId : undefined
  );

  if (isSubstitution) {
    return (
      <RecordReplacePanelV2
        open={true}
        heading="Coverage Change"
        recordLabel="Coverage"
        options={coverageDropDown}
        valueToReplace={
          values.coverage_change?.delete_coverage ?? pickedDelete ?? null
        }
        disableSelect={isEdit}
        onSelectToReplace={(coverageToReplace) => {
          setPickedDelete(coverageToReplace);
          const newValues = produce(values, (draftValues: any) => {
            if (!draftValues.coverage_change) {
              draftValues.coverage_change = { type: null, records: [] };
            }
            draftValues.coverage_change.type = "Change";
            draftValues.coverage_change.delete_coverage = coverageToReplace;
            draftValues.coverage_change.delete_coverage_details = {
              incremental_number: String(coverageToReplace),
            };
          });
          setValues(newValues);
        }}
        onClose={onClose}
      >
        <HabitationalCoverages
          customerId={customerId}
          customerPolicyId={customerPolicyId}
          interestedNumber={
            existing ? (existing.incremental_number as any) : undefined
          }
          onSaveSuccess={saveRecord}
          fromRightPanel
          createdFrom="Policy_Change"
          initialData={existing}
        />
      </RecordReplacePanelV2>
    );
  }

  return (
    <HabitationalCoverageRightPanel
      open={true}
      customerId={customerId}
      customerPolicyId={customerPolicyId}
      interestedNumber={
        existing ? (existing.incremental_number as any) : undefined
      }
      onSaveSuccess={saveRecord}
      createdFrom="Policy_Change"
      onClose={onClose}
    />
  );
};

const ScheduleRecordPanel: React.FC<ISectionPanelProps> = ({
  values,
  customerId,
  customerPolicyId,
  isEdit,
  existing,
  target,
  pickedDelete,
  setPickedDelete,
  setValues,
  saveRecord,
  onClose,
}) => {
  const isSubstitution = target.mode === "Change";
  const { scheduleDropdown, scheduleList } = useHabitationalSchedule(
    isSubstitution ? customerPolicyId : undefined
  );

  if (isSubstitution) {
    return (
      <RecordReplacePanelV2
        open={true}
        heading="Schedule Change"
        recordLabel="Schedule Item"
        options={scheduleDropdown}
        valueToReplace={
          values.schedule_change?.delete_schedule ?? pickedDelete ?? null
        }
        disableSelect={isEdit}
        onSelectToReplace={(scheduleToReplace) => {
          setPickedDelete(scheduleToReplace);
          const matched = scheduleList.find(
            (s: any) =>
              String(s.incremental_number ?? "") === String(scheduleToReplace)
          );
          const newValues = produce(values, (draftValues: any) => {
            if (!draftValues.schedule_change) {
              draftValues.schedule_change = { type: null, records: [] };
            }
            draftValues.schedule_change.type = "Change";
            draftValues.schedule_change.delete_schedule = scheduleToReplace;
            draftValues.schedule_change.delete_schedule_details = {
              incremental_number: String(scheduleToReplace),
              property_number:
                matched?.property_number != null
                  ? String(matched.property_number)
                  : null,
            };
          });
          setValues(newValues);
        }}
        onClose={onClose}
      >
        <HabitationalScheduleDetail
          customerId={customerId}
          customerPolicyId={customerPolicyId}
          incrementalNumber={
            existing ? (existing.incremental_number as any) : undefined
          }
          onSaveSuccess={saveRecord}
          fromRightPanel
          createdFrom="Policy_Change"
          initialData={existing}
        />
      </RecordReplacePanelV2>
    );
  }

  return (
    <HabitationalScheduleDetailRightPanel
      open={true}
      customerId={customerId}
      customerPolicyId={customerPolicyId}
      incrementalNumber={
        existing ? (existing.incremental_number as any) : undefined
      }
      onSaveSuccess={saveRecord}
      createdFrom="Policy_Change"
      onClose={onClose}
    />
  );
};

const InterestedPartyRecordPanel: React.FC<ISectionPanelProps> = ({
  values,
  customerId,
  customerPolicyId,
  isEdit,
  existing,
  target,
  pickedDelete,
  setPickedDelete,
  setValues,
  saveRecord,
  onClose,
}) => {
  const isSubstitution = target.mode === "Change";
  const { interestedPartyDropDown, interestedPartyList } =
    useHabitationalIntrestedParty(isSubstitution ? customerPolicyId : undefined);

  if (isSubstitution) {
    return (
      <RecordReplacePanelV2
        open={true}
        heading="Interested Party Change"
        recordLabel="Interested Party"
        options={interestedPartyDropDown}
        valueToReplace={
          values.interested_party_change?.delete_interested_party ??
          pickedDelete ??
          null
        }
        disableSelect={isEdit}
        onSelectToReplace={(interestedPartyToReplace) => {
          setPickedDelete(interestedPartyToReplace);
          const matched = interestedPartyList.find(
            (ip: any) =>
              String(ip.incremental_number ?? "") ===
              String(interestedPartyToReplace)
          );
          const newValues = produce(values, (draftValues: any) => {
            if (!draftValues.interested_party_change) {
              draftValues.interested_party_change = {
                type: null,
                records: [],
              };
            }
            draftValues.interested_party_change.type = "Change";
            draftValues.interested_party_change.delete_interested_party =
              interestedPartyToReplace;
            draftValues.interested_party_change.delete_interested_party_details =
              {
                incremental_number: String(interestedPartyToReplace),
                location_number:
                  matched?.location_number != null
                    ? String(matched.location_number)
                    : null,
                mortgage_or_loss_payee_name:
                  matched?.mortgage_or_loss_payee_name ?? null,
              };
          });
          setValues(newValues);
        }}
        onClose={onClose}
      >
        <InterestedParties
          customerId={customerId}
          customerPolicyId={customerPolicyId}
          interestedNumber={
            existing ? (existing.incremental_number as any) : undefined
          }
          onSaveSuccess={saveRecord}
          fromRightPanel
          createdFrom="Policy_Change"
          initialData={existing}
        />
      </RecordReplacePanelV2>
    );
  }

  return (
    <InterestedPartiesRightPanel
      open={true}
      customerId={customerId}
      customerPolicyId={customerPolicyId}
      interestedNumber={
        existing ? (existing.incremental_number as any) : undefined
      }
      onSaveSuccess={saveRecord}
      createdFrom="Policy_Change"
      onClose={onClose}
    />
  );
};
